/*  
    Developers: Sakshi Bhise  
    Script: Answer.tsx  
    Date: 06/18/2025  
    Project Name: AskHR  
    Team: DSET  
  
    Purpose of the script:  
    This file defines the main Answer component responsible for rendering a single chat response from the AskHR AI.  
    It handles markdown formatting, references (citations), feedback (like/dislike), retry prompt logic, copy to clipboard, RTL language handling, and feedback dialogs.  
    By centralizing answer display and feedback logic here, the application ensures consistency, security (sanitization), internationalization, and enhanced user engagement.  
*/

//--------------------- Import Dependencies and Contexts ---------------------//  
import { FormEvent, useEffect, useMemo, useState, useContext, useCallback, useRef } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { Checkbox, DefaultButton, Dialog, FontIcon, IconButton, Stack, Text, TooltipDelay, TooltipHost } from "@fluentui/react";
import DOMPurify from 'dompurify';
import { AppStateContext } from '../../state/AppProvider';
import styles from "./Answer.module.css";
import { AskResponse, Citation, Feedback, historyMessageFeedback } from "../../api";
import { parseAnswer } from "./AnswerParser";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from 'remark-supersub';
import { XSSAllowTags } from "../../constants/xssAllowTags";
import { logEvent } from '../../api';
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import i18next from "../../i18n";
import { AuthContext } from "../../state/AuthContext";
import { toast } from "react-toastify";
import { FeedbackDialog, FeedbackDialogContent, MessageBubble } from "@teva/ui";

//--------------------- Component Props Definition ---------------------//  
interface Props {
    answer: AskResponse;  
    type?: 'bot' | 'user';                               // Answer object returned from the API  
    onCitationClicked?: (citedDocument: Citation) => void;// Handler triggered when user clicks a citation  
    onRetry?: (retryPrompt: string) => void;
    onFollowUpClick?: (question: string) => void;           // Handler for retrying a user prompt  
    userPrompt?: string;                                 // The user's question  
}

//--------------------- Main Answer Component ---------------------//  
export const Answer = ({
    answer,
    type='bot',
    onCitationClicked,
    onRetry,
    onFollowUpClick,
    userPrompt
}: Props) => {

    //----------------------------------------------------------------  
    // Helper: Initialize answer feedback from answer object  
    //----------------------------------------------------------------  
    const initializeAnswerFeedback = (answer: AskResponse) => {
        if (answer.message_id == undefined) return undefined;
        if (answer.feedback == undefined) return undefined;
        if (answer.feedback.split(",").length > 1) return Feedback.Negative;
        if (Object.values(Feedback).includes(answer.feedback)) return answer.feedback;
        return Feedback.Neutral;
    };

    //------------------ State Variables and React Hooks ------------------//  
    const { t } = useTranslation();  // t is actual function name is used from useTranslation hook (Internationalisation). Cannot update it to another name
    const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(true); // Citations accordion toggle state  
    const filePathTruncationLimit = 50; // For truncating long reference file paths  
    const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]); // Memoized answer parsing (splits up citations, markdown safely)  
    const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen); // Chevron/collapsible state for citations  
    const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(answer)); // Thumbs up/down feedback state  
    const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false); // Dialog for detailed negative feedback  

    const appStateContext = useContext(AppStateContext); // App-wide state context (feedback, permissions, etc)  
    const FEEDBACK_ENABLED = true; // Toggle whole feedback UX on/off  
    const [tooltipContent, setTooltipContent] = useState<string>(t("answer.copyToClipboard")); // Tooltip text for copy button  
    const markdownRef = useRef<HTMLDivElement>(null); // Ref to rendered markdown, for clipboard copy  
    const country = appStateContext?.state.loggedInUserCountry ? appStateContext?.state.loggedInUserCountry : "";
    const impUserCountry = appStateContext?.state.impUserCountry ? appStateContext.state.impUserCountry : "";
    const hasSubmittedRef = useRef(false);

    const { token } = useContext(AuthContext);
    const globalId = appStateContext?.state.userGId;

    const feedbackContent: FeedbackDialogContent = {
        title: t("answer.submitFeedbackTitle"),
        description: t("answer.submitFeedbackMessage"),
        dropdownLabel: t("unhelpfulFeedback.title"),
        dropdownPlaceholder: t("common.select"),
        options: [
            {
                value: Feedback.MissingCitation,
                label: t("unhelpfulFeedback.missingCitation")
            },
            {
                value: Feedback.WrongCitation,
                label: t("unhelpfulFeedback.wrongCitation")
            },
            {
                value: Feedback.OutOfScope,
                label: t("unhelpfulFeedback.outOfScope")
            },
            {
                value: Feedback.InaccurateOrIrrelevant,
                label: t("unhelpfulFeedback.inaccurateOrIrrelevant")
            },
            {
                value: Feedback.Report,
                label: t("unhelpfulFeedback.reportInappropriate")
            },
            {
                value: Feedback.OtherHarmful,
                label: t("reportFeedback.other")
            }
        ],
        additionalDetailsPlaceholder: t("unhelpfulFeedback.additionalDetailsPlaceholder"),
        consentLabel: t("unhelpfulFeedback.contactConsent"),

        submitButtonLabel: t("answer.submitButton"),
        footerNote: t("answer.feedbackVisibleToOwner"),

        reportValue: Feedback.Report,
        reportSection: {
            title: t("reportFeedback.title"),
            variant: 'radio',
            options: [
                {
                    value: Feedback.HateSpeech,
                    label: t("reportFeedback.hateSpeech")
                },
                {
                    value: Feedback.Violent,
                    label: t("reportFeedback.violent")
                },
                {
                    value: Feedback.Sexual,
                    label: t("reportFeedback.sexual")
                },
                {
                    value: Feedback.Manipulative,
                    label: t("reportFeedback.manipulative")
                }
            ]
        }
    }

    // RTL language check (Arabic, Hebrew, etc, for text direction)  
    const isRTL = (text: string): boolean => {
        const rtlCharRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
        return rtlCharRegex.test(text);
    };

    // Converts an ISO date string into a Date object, forcing UTC parsing.
    // Safari (JavaScriptCore) treats ISO strings without a timezone as local time,
    // while Chrome/V8 treats them as UTC — appending Z avoids that inconsistency.
    const parseTimestampToDate = (timestamp?: string): Date | undefined => {
        if (!timestamp) return undefined;
        let normalized = timestamp.trim().replace(" ", "T");
        if (!/Z$|[+-]\d{2}:?\d{2}$/.test(normalized)) {
            normalized += "Z";
        }
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    };

    //------------------ Effect: Keep chevron and accordion state in sync ------------------//  
    useEffect(() => {
        setChevronIsExpanded(isRefAccordionOpen);
    }, [isRefAccordionOpen]);

    //------------------ Effect: Reset feedback state when answer/context changes ------------------//  
    useEffect(() => {
        if (answer.message_id == undefined) return;
        let currentFeedbackState;
        if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[answer.message_id]) {
            currentFeedbackState = appStateContext?.state.feedbackState[answer.message_id];
        } else {
            currentFeedbackState = initializeAnswerFeedback(answer);
        }
        setFeedbackState(currentFeedbackState);
    }, [appStateContext?.state.feedbackState, answer.message_id]);

    //------------------ Effect: Tooltip resets to (localized) default on language change ------------------//  
    useEffect(() => {
        setTooltipContent(t("answer.copyToClipboard"));
    }, [i18n.language]);

    //----------------------------------------------------------------  
    // Helper: Format citation file path for display/truncated  
    //----------------------------------------------------------------  
    const createCitationFilepath = (citation: Citation, index: number, truncate: boolean = false) => {
        let citationFilename;
        if (citation.url && citation.title) {
            let fileurl = decodeURIComponent(citation.title);
            let lastDotIndex = fileurl.lastIndexOf('.');
            if (lastDotIndex > 0 && lastDotIndex > fileurl.lastIndexOf('/')) {
                fileurl = fileurl.substring(0, lastDotIndex);
            }
            if (truncate && fileurl.length > 0) {
                citationFilename = `${fileurl?.replace(/%20/g, ' ')}`;
            } else {
                citationFilename = `${citation.url}`;
            }
        } else if (citation.url && citation.reindex_id) {
            citationFilename = `${citation.url}`;
        } else if (citation.title) {
            let fileurl = decodeURIComponent(citation.title).split('.').slice(0, -1).join('.');
            citationFilename = `${fileurl?.replaceAll('%20', ' ')}`;
        } else {
            citationFilename = `Citation ${index}`;
        }
        return citationFilename;
    };

    //------------------ Handler: Like button ------------------//  
    const onLikeResponseClicked = async () => {
        if (answer.message_id == undefined) return;
        let newFeedbackState = feedbackState;
        if (feedbackState == Feedback.Positive) {
            newFeedbackState = Feedback.Neutral; // Toggle off  
        } else {
            newFeedbackState = Feedback.Positive;
        }
        appStateContext?.dispatch({ type: 'SET_FEEDBACK_STATE', payload: { answerId: answer.message_id, feedback: newFeedbackState } });
        setFeedbackState(newFeedbackState);
        await historyMessageFeedback(answer.message_id, country, newFeedbackState, token, globalId, undefined, undefined, impUserCountry);
    };

    //------------------ Handler: Dislike button ------------------//  
    const onDislikeResponseClicked = async () => {
        if (answer.message_id == undefined) return;
        let newFeedbackState = feedbackState;
        if (feedbackState === undefined || feedbackState === Feedback.Neutral || feedbackState === Feedback.Positive) {
            newFeedbackState = Feedback.Negative;
            setFeedbackState(newFeedbackState);
            await historyMessageFeedback(answer.message_id, country, Feedback.Negative, token, globalId, undefined, undefined, impUserCountry);
            setIsFeedbackDialogOpen(true); // Open reasons dialog  
        } else {
            newFeedbackState = Feedback.Neutral;
            setFeedbackState(newFeedbackState);
            await historyMessageFeedback(answer.message_id, country, Feedback.Neutral, token, globalId, undefined, undefined, impUserCountry);
        }
        appStateContext?.dispatch({ type: 'SET_FEEDBACK_STATE', payload: { answerId: answer.message_id, feedback: newFeedbackState } });
    };

    //------------------ Helper: Reset dialog state ------------------//  
    const resetFeedbackDialog = () => {
        setIsFeedbackDialogOpen(false);
    };

    // ONLY for the dialog's onClose (X button / escape) — logs, then resets.
    const handleFeedbackDialogClose = () => {
        if (isFeedbackDialogOpen && !hasSubmittedRef.current) {
            logEvent({
                "eventType": "Negative Feedback Escaped",
                "conversationId": appStateContext?.state.currentChat?.id,
                "messageId": answer.message_id,
            }, country, appStateContext?.state?.userGId, token, impUserCountry);
        }
        hasSubmittedRef.current = false;
        resetFeedbackDialog();
    };

    // Copy the answer markdown as both rich HTML and plain text to clipboard, then show tooltip.  
    const copyChatToClipboard = useCallback(async () => {
        if (markdownRef.current) {
            const html = markdownRef.current.innerHTML;
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const plainText = tempDiv.textContent || tempDiv.innerText || '';
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([html], { type: 'text/html' }),
                    'text/plain': new Blob([plainText], { type: 'text/plain' })
                })
            ]).then(() => {
                setTooltipContent(t("answer.copied"));
                setTimeout(() => setTooltipContent(t("answer.copyToClipboard")), 2000);
            }).catch(err => { });
        }
    }, [parsedAnswer.markdownFormatText]);

    //------------------ Handler: Submit negative feedback dialog ------------------//  
   const onSubmitNegativeFeedback = async (payload: any) => {
    if (answer.message_id == undefined) return;
        hasSubmittedRef.current = true;
        if (payload.selected.length > 0) {
            const id = toast.loading(<div>{t("answer.submitFeedbackProgress")}</div>);
            try {
                await historyMessageFeedback(answer.message_id, country, payload.selected.join(",") ?? "", token, globalId, payload.comment?.trim(), payload.consent ?? false, impUserCountry);
                toast.update(id, {
                    render: <div>{t("answer.submitFeedbackSuccessToast")}</div>,
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                });
                resetFeedbackDialog();
            } catch (err) {
                toast.update(id, {
                    render:  <div>{t("answer.submitFeedbackFailToast")}</div>,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                });
            }
        } else {
            toast.warn("Please select atleast one reason.")
        }
    };

    const getFeedbackType = () => {
        if (!feedbackState) return undefined;
        if (feedbackState === Feedback.Negative) return "negative"
        if (feedbackState === Feedback.Positive) return "positive"
        if (feedbackState === Feedback.Neutral) return "neutral"
        return undefined
    }
    //----------------------------------------------------------------  
    // Main Render  
    //----------------------------------------------------------------  
    if(type === 'user'){
        return <MessageBubble
                    type="user"
                    messageContent={{
                        id: answer.message_id ?? '',
                        message: (
                            <div ref={markdownRef}
                            style={{
                                direction: isRTL(userPrompt ? userPrompt : parsedAnswer.markdownFormatText) ? "rtl" : "ltr",
                                textAlign: isRTL(userPrompt ? userPrompt : parsedAnswer.markdownFormatText) ? "right" : "left",
                            }}>
                            {parsedAnswer.markdownFormatText}
                        </div>
                        ),
                        timestamp: parseTimestampToDate(answer.date),
                    }}
                    isRTL={isRTL(t("answer.aiGeneratedDisclaimer"))}
                    onCopy={copyChatToClipboard}
                    copyTooltipText={t("answer.copyToClipboard")}
                    copiedTooltipText={t("answer.copied")}
                />
    }
    return (
        <>
            {/* Main answer card */}
            <MessageBubble
                type="bot"
                messageContent={{
                    id: answer.message_id ?? '',
                    message: (
                        <div ref={markdownRef}
                            style={{
                                direction: isRTL(userPrompt ? userPrompt : parsedAnswer.markdownFormatText) ? "rtl" : "ltr",
                                textAlign: isRTL(userPrompt ? userPrompt : parsedAnswer.markdownFormatText) ? "right" : "left",
                            }}>
                            <ReactMarkdown
                                linkTarget="_blank"
                                remarkPlugins={[remarkGfm, supersub]}
                                children={DOMPurify.sanitize(parsedAnswer.markdownFormatText, { ALLOWED_TAGS: XSSAllowTags })}
                                className={styles.answerText}
                            />
                        </div>
                    ),
                    sources: parsedAnswer.citations.map((c, i) => ({
                        id: String(i + 1),
                        label: c.title ?? '',
                        url: c.url ?? '',
                    })),
                    followUpQuestions: answer.followUpQuestions,
                    feedbackType: getFeedbackType(),
                    timestamp: parseTimestampToDate(answer.date)
                }}
                sourcesText={t("answer.references", { count: parsedAnswer.citations.length })}
                isRTL={isRTL(t("answer.aiGeneratedDisclaimer"))}
                disclaimerText={t("answer.aiGeneratedDisclaimer")}
                onFollowUpClick={onFollowUpClick}
                onSourceClick={(id) => {
                    const citation = parsedAnswer.citations[parseInt(id) - 1];
                    if (citation && onCitationClicked) onCitationClicked(citation);
                }}
                onRegenerate={() => onRetry && onRetry(userPrompt ?? '')}
                onCopy={copyChatToClipboard}
                onFeedback={(type) => {
                    if ((type === "neutral" && feedbackState === Feedback.Negative) || type === 'negative') {
                        onDislikeResponseClicked()
                    } else if ((type === "neutral" && feedbackState === Feedback.Positive) || type === 'positive') {
                        onLikeResponseClicked()
                    }
                }}
                copyTooltipText={t("answer.copyToClipboard")}
                copiedTooltipText={t("answer.copied")}
                reloadTooltipText={t("answer.retryThisPrompt")}
                thumbsUpTooltipText={t("answer.likeThisResponse")}
                thumbsDownTooltipText={t("answer.dislikeThisResponse")}
                regenerateTooltipText={t("common.retry")}

            />
            {/* Feedback dialog for submitting negative feedback or abuse */}
            <FeedbackDialog
                isOpen={isFeedbackDialogOpen}
                content={feedbackContent}
                isRTL={isRTL(t("common.retry"))}
                onClose={handleFeedbackDialogClose}
                onSubmit={onSubmitNegativeFeedback}
                requireConsentToSubmit={true}    
            />
        </>
    );
};  